/**
 * IdleReel — decorative TMDb-poster carousel behind the OFF-state banner.
 *
 * Behaviour:
 *   - Fetches `/api/tmdb/idle-reel?n=6` when mounted (i.e. CRT just
 *     went to OFF). Returns up to 6 random poster URLs pulled from
 *     tapes that already carry TMDb metadata.
 *   - Sits invisible for 30 seconds. If the viewer powers on before
 *     then, we never render anything and no network hit is wasted.
 *   - After the 30s idle grace, fades in the first poster (blurred +
 *     darkened) as a full-viewport backdrop and starts a slow 8s
 *     cross-fade between the returned poster URLs.
 *   - Silently no-ops when the library has zero TMDb-backed tapes —
 *     the OFF-state remains the classic black + banner look.
 *
 * The whole component is <button>-safe: `pointer-events-none` on the
 * image layer means clicks still hit the underlying "click to tune"
 * click-target in CRT.js.
 */
import React, { useEffect, useState } from "react";

const IDLE_MS = 30_000;
const ROTATE_MS = 8_000;

export default function IdleReel({ apiBase }) {
  const [posters, setPosters] = useState([]);
  const [idle, setIdle] = useState(false);
  const [index, setIndex] = useState(0);

  // 30s idle timer starts on mount. Clears on unmount so powering
  // the CRT on kills the countdown cleanly.
  useEffect(() => {
    const t = setTimeout(() => setIdle(true), IDLE_MS);
    return () => clearTimeout(t);
  }, []);

  // Fetch the reel only once we're actually about to display it.
  // Small optimisation: a viewer who never idles never hits the API.
  useEffect(() => {
    if (!idle) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/tmdb/idle-reel?n=6`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data.posters)) {
          setPosters(data.posters);
        }
      } catch {
        /* silent — the reel is decorative, offline is fine */
      }
    })();
    return () => { cancelled = true; };
  }, [idle, apiBase]);

  // Slow rotation once we have at least 2 posters. Single-poster
  // libraries skip rotation and just show the one image.
  useEffect(() => {
    if (posters.length < 2) return;
    const iv = setInterval(
      () => setIndex((i) => (i + 1) % posters.length),
      ROTATE_MS
    );
    return () => clearInterval(iv);
  }, [posters.length]);

  if (!idle || posters.length === 0) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none overflow-hidden"
      data-testid="idle-reel"
      aria-hidden="true"
    >
      {posters.map((p, i) => (
        <img
          key={p.url + i}
          src={p.url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[3000ms]"
          style={{
            opacity: i === index ? 0.28 : 0,
            filter: "blur(6px) saturate(0.85)",
          }}
        />
      ))}
      {/* Dark vignette so the phosphor banner keeps contrast against
          any bright poster (Pixar / cartoon posters are especially
          light). Sits above the images, below the banner. */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/50" />
    </div>
  );
}
